import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  Sport,
  SupportedCurrency,
} from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { BodyL, BrandL } from '@sorare/core/src/atoms/typography';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useIsVisibleInViewport } from '@sorare/core/src/hooks/ui/useIsVisibleInViewport';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import {
  Sum,
  useMonetaryAmount,
} from '@sorare/core/src/hooks/useMonetaryAmount';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { cart, glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { getSortedCards } from '@sorare/core/src/lib/players';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { SmoothBodyScroll } from '@sorare/core/src/style/utils';

import { useConversionCreditForPrice } from '@sorare/marketplace/src/hooks/useConversionCreditForPrice';

import { ExpertBanner } from '@sorare/gameplay/src/components/onboarding/ExpertBanner';

import { MarketGuidanceLayout } from '#src/components/onboarding/MarketGuidanceLayout/index.tsx';
import { PromoButton } from '#src/components/onboarding/PromoButton/index.tsx';

import { BackToCommon } from './BackToCommon/index.tsx';
import { FirstLineupPreview } from './FirstLineupPreview/index.tsx';
import { LastSales } from './LastSales/index.tsx';
import { Prizes } from './Prizes/index.tsx';
import { TeamScoreBlock } from './TeamScoreBlock/index.tsx';
import type {
  FirstLineupEdition_currentUser,
  FirstLineupEdition_so5Leaderboard,
} from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 3, center: true })`
  max-width: min(calc(65 * var(--unit)), 100%);
  align-items: stretch;
`;
const TitleWrapper = styled(Vertical).attrs({ gap: 2, center: true })`
  &:not(:first-child) {
    padding-top: var(--triple-unit);

    @media ${tabletAndAbove} {
      padding-top: var(--unit);
    }
  }
`;
const TileWrapper = styled(Vertical).attrs({ gap: 3, center: true })`
  width: 100%;
  align-items: stretch;
  isolation: isolate;
`;
const Separator = styled.div`
  width: 100%;
  height: 0;
  border-top: 1px solid var(--c-nd-50);

  @media ${tabletAndAbove} {
    visibility: hidden;
  }
`;
const LastSalesWrapper = styled.div`
  padding-bottom: var(--quadruple-unit);
  height: 100%;
`;
const StyledSpan = styled.span`
  color: var(--c-nl-600);
`;
const ButtonsWrapper = styled(Vertical)`
  width: 100%;
  transition: all 300ms ease-in-out;
  @media ${tabletAndAbove} {
    width: auto;
  }

  &.translated {
    transform: translateY(calc(5 * var(--unit)));
  }
`;
const NotReadyButtonWrapper = styled(Vertical)`
  width: 100%;
  opacity: 1;
  transform: scale(1);
  transition: all 300ms ease-in-out;

  &.hidden {
    opacity: 0;
    transform: scale(0.3);
  }
`;

type Props = {
  currentUser?: FirstLineupEdition_currentUser | null;
  so5Leaderboard?: FirstLineupEdition_so5Leaderboard;
  onSubmit: () => void;
  progress: number;
  backUrl: string;
  refetch: () => Promise<void>;
  cartQueryLoading?: boolean;
};
export const FirstLineupEdition = ({
  currentUser,
  so5Leaderboard,
  onSubmit,
  progress,
  backUrl,
  refetch,
  cartQueryLoading,
}: Props) => {
  const { toMonetaryAmount } = useMonetaryAmount();
  const isTabletAndAbove = useIsTabletAndAbove();
  const track = useEvents();
  const { handleRef, isVisible } = useIsVisibleInViewport<HTMLDivElement>();
  const { primaryColor } = so5Leaderboard?.so5League || {};
  const cardsFromCart =
    currentUser?.myCart?.mainCartItems
      .map(({ card }) => card)
      .filter(r => isType(r, 'Card')) || [];
  const cards = getSortedCards(cardsFromCart);

  const totalAmount = Sum(
    cards
      .map(({ livePrimaryOffer }) => livePrimaryOffer?.price)
      .filter(Boolean)
      .map(cc => toMonetaryAmount(cc))
  );
  totalAmount.referenceCurrency = SupportedCurrency.EUR;

  const { absoluteConversionCreditMonetaryAmount, priceDiscountedAmount } =
    useConversionCreditForPrice({
      price: totalAmount,
      sport: Sport.FOOTBALL,
    });

  const { main: amountSaved } = useAmountWithConversion({
    monetaryAmount: absoluteConversionCreditMonetaryAmount,
  });
  const { main: amountPaid } = useAmountWithConversion({
    monetaryAmount: priceDiscountedAmount,
  });
  const { commonGameOnboarded, availableOnboardingLeaderboards } =
    currentUser?.footballUserProfile || {};

  return (
    <MarketGuidanceLayout
      center
      progress={progress}
      primaryBackgroundColor={primaryColor}
      backUrl={backUrl}
      cta={
        <ButtonsWrapper className={classNames({ translated: isVisible })}>
          <PromoButton
            fullWidth={!isTabletAndAbove}
            color="secondary"
            size="large"
            onClick={() => {
              track('Click Checkout In Guidance First Lineup', {});
              onSubmit();
            }}
            conversionCredit={currentUser?.onboardingConversionCredit}
            customMessage={
              <Horizontal>
                <FormattedMessage
                  id="FirstLineupEdition.cta.promo"
                  defaultMessage="{amount} saved"
                  values={{
                    amount: amountSaved,
                  }}
                />
              </Horizontal>
            }
          >
            <FormattedMessage {...cart.checkout} />{' '}
            <StyledSpan>— {amountPaid}</StyledSpan>
          </PromoButton>
          <NotReadyButtonWrapper
            className={classNames({
              hidden: isVisible || !availableOnboardingLeaderboards,
            })}
          >
            <Button to="#not-ready" size="medium" color="quaternary" blur>
              <FormattedMessage
                id="FirstLineupEdition.notReady"
                defaultMessage="Not ready?"
              />
            </Button>
          </NotReadyButtonWrapper>
        </ButtonsWrapper>
      }
    >
      <Root>
        <TitleWrapper>
          <StarBall />
          <Vertical center>
            <BrandL as="h2">
              <FormattedMessage
                id="FirstLineupEdition.firstBlock.title"
                defaultMessage="Suggested team"
              />
            </BrandL>
            <BodyL className="text-center">
              <FormattedMessage
                id="RecommendedPlayers.title3"
                defaultMessage="Your lineup for the next {leagueName} matchday."
                values={{
                  leagueName: so5Leaderboard?.so5League?.displayName,
                }}
              />
            </BodyL>
          </Vertical>
        </TitleWrapper>
        <TileWrapper>
          <FirstLineupPreview
            cards={cards}
            so5Leaderboard={so5Leaderboard}
            onCartUpdated={refetch}
            cartQueryLoading={cartQueryLoading}
            backgroundUrl={so5Leaderboard?.stadiumUrl}
          />
        </TileWrapper>
        <Separator />
        <TitleWrapper>
          <StarBall />
          <Vertical center>
            <BrandL>
              <FormattedMessage {...glossary.trade} />
            </BrandL>
            <BodyL className="text-center">
              <FormattedMessage
                id="FirstLineupEdition.trade.title2"
                defaultMessage="You own these cards forever and can sell them to other Managers anytime."
              />
            </BodyL>
          </Vertical>
        </TitleWrapper>
        <Scrollable
          itemToDisplay={1}
          itemGap={isTabletAndAbove ? 4 : 2}
          dots={cards.map(({ slug }) => slug)}
          overhang={isTabletAndAbove ? 8 : 3}
        >
          {cards.map(card => (
            <LastSalesWrapper key={card.slug}>
              <LastSales card={card} />
            </LastSalesWrapper>
          ))}
        </Scrollable>
        <Separator />
        <Vertical center gap={2} className="text-center">
          <StarBall />
          <Vertical center>
            <BrandL as="h2">
              <FormattedMessage {...glossary.win} />
            </BrandL>
            <BodyL>
              <FormattedMessage
                id="FirstLineupEdition.win.subtitle2"
                defaultMessage="Cash, cards, jerseys, match tickets and more!"
              />
            </BodyL>
          </Vertical>
        </Vertical>
        <TileWrapper>
          <Prizes so5Leaderboard={so5Leaderboard} />
          {!!so5Leaderboard && (
            <TeamScoreBlock
              cardSlugs={cards.map(({ slug }) => slug)}
              so5LeaderboardType={so5Leaderboard?.so5LeaderboardType}
            />
          )}
        </TileWrapper>
        <Separator />
        {!!availableOnboardingLeaderboards && (
          <>
            <TitleWrapper className="text-center" id="not-ready">
              <StarBall />
              <Vertical center>
                <BrandL>
                  <FormattedMessage
                    id="FirstLineupEdition.notReady"
                    defaultMessage="Not ready?"
                  />
                </BrandL>
                <BodyL ref={handleRef}>
                  {commonGameOnboarded ? (
                    <FormattedMessage
                      id="BackToCommon.subtitle2"
                      defaultMessage="Continue playing Beginner"
                    />
                  ) : (
                    <FormattedMessage
                      id="BackToCommon.subtitle"
                      defaultMessage="Play Beginner for free to learn the basics of Sorare."
                    />
                  )}
                </BodyL>
              </Vertical>
            </TitleWrapper>
            <BackToCommon leagueSlug={so5Leaderboard?.so5League?.slug} />
          </>
        )}
        <ExpertBanner />
      </Root>
      <SmoothBodyScroll />
    </MarketGuidanceLayout>
  );
};

FirstLineupEdition.fragments = {
  currentUser: gql`
    fragment FirstLineupEdition_currentUser on CurrentUser {
      slug
      onboardingConversionCredit(sport: FOOTBALL) {
        id
        percentageDiscount
        ...PromoButton_conversionCredit
      }
      myCart(sport: $sport) {
        id
        mainCartItems {
          id
          card {
            slug
            livePrimaryOffer {
              id
              price {
                referenceCurrency
              }
            }
            ...LastSales_anyCard
            ...FirstLineupPreview_anyCard
          }
        }
      }
      footballUserProfile {
        id
        commonGameOnboarded
        availableOnboardingLeaderboards(rarity: common)
      }
    }
    ${LastSales.fragments.anyCard}
    ${FirstLineupPreview.fragments.anyCard}
    ${PromoButton.fragments.conversionCredit}
  ` as TypedDocumentNode<FirstLineupEdition_currentUser>,
  so5Leaderboard: gql`
    fragment FirstLineupEdition_so5Leaderboard on So5Leaderboard {
      slug
      stadiumUrl
      so5LeaderboardType
      so5League {
        slug
        displayName
        primaryColor: color(variant: PRIMARY)
      }
      ...FirstLineupPreview_so5Leaderboard
      ...Prizes_so5Leaderboard
    }
    ${FirstLineupPreview.fragments.so5Leaderboard}
    ${Prizes.fragments.so5Leaderboard}
  ` as TypedDocumentNode<FirstLineupEdition_so5Leaderboard>,
};
